/**
 * PR1 wave 1 — Calendar & Diary API types.
 *
 * Backend contract (another workstream):
 * - GET    /api/calendar/events?from=<ISO>&to=<ISO>&kinds=a,b → 200 `{ events: CalendarEvent[] }`
 * - POST   /api/calendar/events                              → 201 `{ event }`
 * - PATCH  /api/calendar/events/:id                          → 200 `{ event }`
 * - GET    /api/calendar/events/:id                          → 200 `{ event }`
 * Non-2xx responses carry `{ error: code }`; callers must treat any failure
 * as "calendar unavailable" and never crash the dashboard.
 *
 * Methods live on the shared `api` object in `@/api/client` (fetchJson
 * pattern); this module is the type home, same as portalModeration.ts.
 */

export type CalendarKind = 'viewing' | 'meeting' | 'todo' | 'call' | 'custom'

export type CalendarEventStatus = 'confirmed' | 'pending' | 'cancelled' | 'completed'

export type CalendarVisibility = 'personal' | 'team'

export type CalendarSource = 'manual' | 'ai' | 'system'

export interface CalendarAttendee {
  id: string
  event_id: string
  contact_id: string | null
  user_id: string | null
  role: 'organizer' | 'attendee' | 'proxy'
  rsvp: 'accepted' | 'declined' | 'pending'
  responded_at: string | null
}

/** Wave 6b — series rule accepted by create/PATCH; exactly one of count/until. */
export interface CalendarRecurrenceRule {
  freq: 'daily' | 'weekly' | 'monthly'
  interval?: number
  /** Weekly only, JS weekdays 0=Sunday..6=Saturday; required for weekly. */
  byweekday?: number[]
  count?: number
  until?: string
}

export interface CalendarEvent {
  id: string
  tenant_id: string
  owner_user_id: string
  kind: CalendarKind
  title: string
  description: string | null
  starts_at: string
  ends_at: string | null
  timezone: string
  all_day: boolean
  location_text: string | null
  location_link: string | null
  status: CalendarEventStatus
  visibility: CalendarVisibility
  source: CalendarSource
  contact_id: string | null
  property_id: string | null
  opportunity_id: string | null
  assigned_proxy_user_id: string | null
  attendees: CalendarAttendee[]
  /**
   * Wave 6b recurrence. Series rows carry `recurrence`; window occurrences of
   * a series additionally carry series_id, is_recurrence_instance, and
   * occurrence_start, with a derived id of `${series_id}#${occurrence_start}`.
   */
  series_id?: string | null
  is_recurrence_instance?: boolean
  occurrence_start?: string | null
  recurrence?: CalendarRecurrenceRule | null
}

export interface CalendarEventListParams {
  from: string
  to: string
  kinds?: CalendarKind[]
}

/** Payload for POST /api/calendar/events — nullable FKs stay null in wave 1. */
export interface CalendarEventInput {
  kind: Extract<CalendarKind, 'meeting' | 'todo'>
  title: string
  starts_at: string
  ends_at?: string | null
  timezone?: string
  all_day?: boolean
  location_text?: string | null
  location_link?: string | null
  description?: string | null
  visibility?: CalendarVisibility
  status?: CalendarEventStatus
  source?: CalendarSource
  property_id?: string | null
  contact_id?: string | null
  opportunity_id?: string | null
}

/** Payload for PATCH /api/calendar/events/:id. */
export type CalendarEventPatch = Partial<
  Pick<
    CalendarEvent,
    | 'kind'
    | 'title'
    | 'description'
    | 'starts_at'
    | 'ends_at'
    | 'timezone'
    | 'all_day'
    | 'location_text'
    | 'location_link'
    | 'status'
    | 'visibility'
  >
>

/**
 * PR1 wave 4 — Calendar Settings + autoresponse templates.
 *
 * Backend contract (merged in place):
 * - GET /api/calendar/settings → 200 `{ settings: CalendarSettings }`
 *   (absent row → UTC defaults; `operating_hours: null` = no hours limit)
 * - PUT /api/calendar/settings (all fields required) → 200 `{ settings }`;
 *   400 `{ error: 'calendar_invalid_settings', details }`
 * - GET/POST /api/calendar/autoresponse-templates → `{ templates }` / 201 `{ template }`
 * - PATCH/DELETE /api/calendar/autoresponse-templates/:id → `{ template }` / `{ deleted: true }`
 *   (404 `{ error: 'calendar_template_not_found' }`)
 *
 * operating_hours is the engine-pinned shape: JS weekday keys (0=Sunday..6=
 * Saturday), one window per open weekday, absent key = closed, null = no
 * limit. All-closed must be sent as `null`.
 */

/** One open window on a weekday, "HH:MM" 24-hour values. */
export interface CalendarOperatingHoursWindow {
  start: string
  end: string
}

/** Engine-pinned shape: keys are JS weekdays "0".."6"; absent = closed. */
export type CalendarOperatingHours = Record<string, CalendarOperatingHoursWindow>

export interface CalendarSettings {
  timezone: string
  operating_hours: CalendarOperatingHours | null
  buffer_minutes: number
  min_notice_minutes: number
  max_events_per_day: number
  ai_guidelines: string
}

/** PUT body — same shape, all fields required. */
export type CalendarSettingsInput = CalendarSettings

export type AutoresponseTrigger = 'on_booked' | 'on_reschedule' | 'on_cancel'

export type AutoresponseChannel = 'whatsapp' | 'in_app' | 'email'

export interface AutoresponseTemplate {
  id: string
  tenant_id: string
  owner_user_id: string
  name: string
  trigger: AutoresponseTrigger
  channel: AutoresponseChannel
  body: string
  active: boolean
  created_at: string
  updated_at: string
}

/** POST body — `active` defaults server-side; the dialog always sends it. */
export type AutoresponseTemplateInput = Pick<
  AutoresponseTemplate,
  'name' | 'trigger' | 'channel' | 'body' | 'active'
>

/** PATCH body — partial, at least one field. */
export type AutoresponseTemplatePatch = Partial<AutoresponseTemplateInput>

/** Per-kind accent colour, mapped to existing broadcast semantic tokens. */
export const KIND_COLORS: Record<CalendarKind, string> = {
  viewing: 'var(--lc-action-primary)',
  meeting: 'var(--lc-channel-linkedin)',
  todo: 'var(--lc-status-underOffer-dot)',
  call: 'var(--lc-channel-whatsapp)',
  custom: 'var(--lc-accent)',
}

/**
 * PR1 wave 5 — Team calendar.
 *
 * Backend contract (another workstream):
 * - GET /api/calendar/team?from=<ISO>&to=<ISO> → 200
 *   `{ events: TeamCalendarEvent[], members: CalendarTeamMember[] }`
 *   Team-VISIBILITY events of all agency members in range, plus the member
 *   roster. Empty `{ events: [], members: [] }` for a tenant with no team
 *   events (solo agencies and personal tenants included).
 */
export interface CalendarTeamMember {
  user_id: string
  name: string
}

export interface TeamCalendarEvent extends CalendarEvent {
  owner_name?: string
}

export interface CalendarTeamEventsResponse {
  events: TeamCalendarEvent[]
  members: CalendarTeamMember[]
}

/**
 * Per-member accent palette for the team view: seven distinct hues, every
 * one an existing broadcast token (channel + status-dot + accent families —
 * same source as KIND_COLORS above). No raw hex literals here; the tokens
 * carry both light and dark values.
 */
export const MEMBER_COLORS: readonly string[] = [
  'var(--lc-channel-linkedin)',
  'var(--lc-channel-instagram)',
  'var(--lc-status-closed-dot)',
  'var(--lc-status-underOffer-dot)',
  'var(--lc-channel-bayut)',
  'var(--lc-channel-property_finder)',
  'var(--lc-accent)',
]

/** Stable member colour: djb2 hash of the owner id into MEMBER_COLORS. */
export function memberColor(ownerUserId: string): string {
  let hash = 5381
  for (let i = 0; i < ownerUserId.length; i++) {
    hash = ((hash << 5) + hash + ownerUserId.charCodeAt(i)) | 0
  }
  return MEMBER_COLORS[Math.abs(hash) % MEMBER_COLORS.length]
}
